/**
 * ToastContainer — renders stacked toasts in the top-right corner.
 * Mounted once in the authenticated shell (AuthAppLayout).
 */

import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react'
import { useToastStore, type ToastVariant } from '@/store/toastStore'

const VARIANT_STYLES: Record<
  ToastVariant,
  { ring: string; icon: string; Icon: typeof CheckCircle2 }
> = {
  success: {
    ring: 'border-l-4 border-green-500',
    icon: 'text-green-600 dark:text-green-400',
    Icon: CheckCircle2,
  },
  error: {
    ring: 'border-l-4 border-red-500',
    icon: 'text-red-600 dark:text-red-400',
    Icon: AlertCircle,
  },
  warning: {
    ring: 'border-l-4 border-orange-500',
    icon: 'text-orange-600 dark:text-orange-400',
    Icon: AlertTriangle,
  },
  info: {
    ring: 'border-l-4 border-primary-500',
    icon: 'text-primary-600 dark:text-primary-400',
    Icon: Info,
  },
}

export default function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts)
  const dismiss = useToastStore((s) => s.dismiss)

  if (toasts.length === 0) return null

  return (
    <div
      className="fixed top-4 right-4 z-[10000] flex flex-col gap-3 w-full max-w-sm pointer-events-none"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => {
        const { ring, icon, Icon } = VARIANT_STYLES[t.variant]
        return (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto flex items-start gap-3 p-4 rounded-xl shadow-xl bg-white dark:bg-surface-elevated ${ring} animate-in slide-in-from-right-4 fade-in duration-200`}
          >
            <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${icon}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">{t.title}</p>
              {t.description && (
                <p className="text-sm text-text-secondary mt-0.5">{t.description}</p>
              )}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="flex-shrink-0 text-text-tertiary hover:text-text-primary transition rounded-md p-0.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
